import React, { useContext } from "react";

import useStructureParams from "../../lib/useStructureParams";
import { filterAndSortChildren, wardNamesCleanup } from "../../lib/structures";
import { useIssueCounts, type IssueCount } from "../../data/useIssueCounts";

import { Col, Row } from "react-bootstrap";

import PageTitle from "../PageTitle";
import Breadcrumbs from "../Breadcrumbs";
import PageHeader from "../PageHeader";
import ViewButtons from "../ViewButtons";
import { LoadingSpinner } from "../spinners";

// import Map from "./Map";
import VDCategoryFilter from "../VDCategoryFilter";
import { DashboardContext } from "../../data/context";
import HeaderLayout from "../HeaderLayout";

import { getThemeColour } from "./themeColours";
import IssueSunburst from "./IssueSunburst";
import RankedIssueTable from "./RankedIssueTable";
import ChildIssueTable from "./ChildIssueTable";
import Map from "../Map";

const getBiggestTheme = (
  issueCounts: IssueCount[],
): { theme: string; count: number } => {
  const themeCounts: Record<string, number> = {};
  for (const issue of issueCounts) {
    if (themeCounts[issue.theme] == null) {
      themeCounts[issue.theme] = issue.count;
    } else {
      themeCounts[issue.theme] += issue.count;
    }
  }
  let biggestTheme: { theme: string; count: number } = {
    theme: "(none)",
    count: 0,
  };
  for (const [theme, count] of Object.entries(themeCounts)) {
    if (count > biggestTheme.count) {
      biggestTheme = { theme, count };
    }
  }
  return biggestTheme;
};

export default function IssueView(): JSX.Element {
  const { filterLabel } = useContext(DashboardContext);
  // get parameters from the URL
  const { code, type } = useStructureParams();

  // get stats from the GraphQL API
  const { stats, loading } = useIssueCounts({ type, code, filterLabel });
  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />;
  }

  // filter/sort children appropriately according to structure type
  const children = filterAndSortChildren(stats.type, stats.children)
    .map(wardNamesCleanup)
    .map((c) => ({
      ...c,
      biggestTheme: getBiggestTheme(c.microtargeting.issuecounts),
    }))
    .sort((a, b) => {
      return a.type === "ward" || a.type === "votingdistrict"
        ? (a.code ?? "").localeCompare(b.code ?? "")
        : (a.name ?? "").localeCompare(b.name ?? "");
    });

  const issues = [...stats.microtargeting.issuecounts].sort((a, b) => {
    return b.count - a.count !== 0
      ? b.count - a.count
      : a.issue.localeCompare(b.issue);
  });

  const mapColor = (code: string): string => {
    const c = children.find((c) => c.code === code);
    if (c == null) return "#ffffff";
    return getThemeColour(c.biggestTheme.theme);
  };

  return (
    <>
      <PageTitle title={stats.name} />
      <Breadcrumbs structure={stats} suffix="/issues" />
      <HeaderLayout
        element1={<PageHeader structure={stats} />}
        element2={<ViewButtons structure={stats} section="issues" />}
        element3={<VDCategoryFilter counts={stats.counts} />}
      />
      <Row>
        <Col md={6}>
          <div style={{ maxWidth: "600px", margin: "0 auto" }}>
            <IssueSunburst issues={issues} />
          </div>
        </Col>
        <Col md={6}>
          <Map
            type={type}
            code={code}
            childStructures={stats.children}
            childColourFn={mapColor}
          />
        </Col>
      </Row>
      <br />
      <Row>
        <Col md={6}>
          <h4>Issues in {stats.name}</h4>
          <RankedIssueTable
            issues={issues}
            totalSurveys={stats.microtargeting.phase1.current}
          />
        </Col>

        <Col md={6}>
          <h4>Biggest issue by {children[0].type}</h4>
          <ChildIssueTable children={children} />
        </Col>
      </Row>
    </>
  );
}
